import { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { toPng } from "html-to-image";

// store
import useBrainStore from "@/store/_brainEmStore";
import useLinkStore from "@/store/_linkStore";
import { organismImgData } from "@/data/OrganismImgData";

// styles
import "./ExportHighlights.css";

// -----------------------------------------------------------------------------

export default function ExportHighlightsPreview({ isLinkMode, onComplete }) {
	const yearsToEmulation = 8;
	const totalCost = "1.20 B";
	// const imgStyle = "sciIllustration";
	const imgStyle = "photo";

	// Determine which store hook to use
	const useStore = isLinkMode ? useLinkStore : useBrainStore;
	// Select the organism state
	const organism = useStore((state) => state.organism);

	// Destructure appropriate data based on imgStyle
	// img file and class are enough, no other data needed for png export
	const {
		imageSciLarge,
		classNameSciLarge,
		imagePhotoLarge,
		classNamePhotoLarge,
	} = organismImgData[organism];

	// Determine the specific image and class to use based on imgStyle prop
	let imageToUse;
	let classToUse;

	if (imgStyle === "sciIllustration") {
		imageToUse = imageSciLarge;
		classToUse = classNameSciLarge;
	} else {
		imageToUse = imagePhotoLarge;
		classToUse = classNamePhotoLarge;
	}

	const cardRef = useRef(null);

	// -----------------------------------------------------------------------------

	useEffect(() => {
		async function getCardIdAndExport() {
			// Now export the card as PNG.
			if (cardRef.current) {
				try {
					const dataUrl = await toPng(cardRef.current, {
						pixelRatio: 3,
						cacheBust: true,
						// skipFonts: true,
					});

					// Create a temporary download link and trigger download.
					const link = document.createElement("a");
					link.download = "brainEm-highlights.png";
					link.href = dataUrl;
					link.click();

					// catch
				} catch (error) {
					console.error("Export failed:", error);
				} finally {
					// Notify parent that we're done
					onComplete();
				}
			}
		}

		// small initial delay for images and fonts to render
		const timer = setTimeout(getCardIdAndExport, 50);
		return () => clearTimeout(timer);
	}, [onComplete]);

	// -----------------------------------------------------------------------------

	return (
		<div ref={cardRef} className="highlights-card">
			{/* IMG */}
			<div className="card-img-container">
				{/* biome-ignore lint/a11y/useAltText: <explanation> */}
				<img src={imageToUse} className={classToUse} />
			</div>

			<div className="data-container">
				{/* ORGANISM */}
				<div className="organism-highlight-container">
					{/* Heading */}
					<p className="highlight-heading">Organism</p>
					{/* Big value */}
					<p className="highlight-value">{organism}</p>
				</div>

				{/* TIME TO EMULATION */}
				<div className="numerical-highlight-container border-right">
					{/* Heading */}
					<p className="highlight-heading">Time to Emulation</p>
					{/* Big value */}
					<p className="highlight-value">{yearsToEmulation} years</p>
				</div>

				{/* TOTAL COST */}
				<div className="numerical-highlight-container">
					{/* Heading */}
					<p className="highlight-heading">Total Cost in USD</p>
					{/* Big value */}
					<p className="highlight-value">{totalCost}</p>
				</div>
			</div>

			{/* Card footer */}
			<div className="card-footer">
				<p>brainem.co</p>
				<p>Card ID: 1234</p>
			</div>
		</div>
	);
}

ExportHighlightsPreview.propTypes = {
	store: PropTypes.object.isRequired,
	onComplete: PropTypes.func.isRequired,
};
